import type { ReactNode } from "react";
import "./screens.css";
import type { BuyNotice, PurchaseRecord } from "../game/bakeryStore";
import { fmtCompact } from "../game/format";

export function ProgressBar({ pct, gold }: { pct: number; gold?: boolean }) {
  const v = Math.min(100, Math.max(0, pct));
  return (
    <div className="cb-track" role="progressbar" aria-valuenow={Math.round(v)} aria-valuemin={0} aria-valuemax={100}>
      <div className={gold ? "cb-fill-gold" : "cb-fill"} style={{ width: `${v}%` }} />
    </div>
  );
}

export function Panel({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`cb-panel ${className}`}>{children}</div>;
}

export function Toggle({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} className="cb-toggle" onClick={onClick} />
  );
}

export function AvatarDot({ name, color }: { name: string; color: string }) {
  return (
    <span className="cb-ava-dot" style={{ background: color }} aria-hidden="true">
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

// Buy-certainty UX: every buy resolves to exactly one visible toast —
// pending keeps pendingKeys certainty but renders playful: instant optimistic
// level + cost deduct stay visible, card shimmers + cookie pulses with a
// non-blocking "Buying…/Building…" shimmer (no spinner). Success shows new level +
// cost, errors show need vs have + gate reason.
export function BuyToast({ notice }: { notice: BuyNotice | null }) {
  if (!notice) return null;
  return (
    <div className={notice.ok ? "cb-toast ok" : "cb-toast err"} role={notice.ok ? "status" : "alert"} aria-live="polite">
      {notice.text}
    </div>
  );
}

// Owned confirmation ledger: last confirmed purchases (level + cost).
export function PurchaseLedger({ history, kinds }: { history: PurchaseRecord[]; kinds: string[] }) {
  const rows = history.filter((h) => kinds.includes(h.kind)).slice(0, 5);
  if (rows.length === 0) return null;
  return (
    <div className="cb-ledger" role="status" aria-live="polite" aria-label="Recent purchases">
      <span className="cb-ledger-title">🧾 Recent purchases</span>
      {rows.map((h) => (
        <div key={h.key} className="cb-ledger-row">
          <span>✅ {h.label}{typeof h.level === "number" ? ` → Lv ${h.level}` : ""}</span>
          {h.cost !== undefined && <span className="dim">{fmtCompact(h.cost)}</span>}
        </div>
      ))}
    </div>
  );
}
